import { Component, DestroyRef, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { HospitalNames } from '@shared/models/hospitalTypes.model';
import { SubscriptionManager } from '@shared/services/subscription-manager.service';
import { forkJoin } from 'rxjs';
import { ErrorService } from '../../services/error.service';
import { PopupManagerService } from '../../services/popup-manager.service';
import { RankingService } from '../../services/ranking.service';
import { SkillService } from '../../services/skill.service';
import { UserService } from '../../services/user.service';
import { InstanceIdService } from '@shared/services/instance-id.service';

@Component({
  selector: 'app-intialize-profile-skills',
  templateUrl: './intialize-profile-skills.component.html',
  styleUrls: ['./intialize-profile-skills.component.scss'],
  standalone: false,
})
export class IntializeProfileSkillsComponent implements OnInit, OnChanges {
  private instanceId: string;

  @Output() public skillsValidation = new EventEmitter();

  @Input() public profile: any;

  @Input() public openType: string = '';

  @Input() public newServiceId: string | undefined;

  @Input() public isWithoutCloseBtn: boolean = false;

  @Output() public onClose = new EventEmitter();

  public selected = '1';

  public specialtiesWithRanks: any;

  public skillsOfProfileFromOtherHospitals: any[] | undefined;

  public isLoading: boolean = true;

  public imgLinks: string[] | undefined;

  public listOfRatings: any[] = [];

  public osComponentOptions = {
    className: 'os-theme-dark custom-sidebar',
    nativeScrollbarsOverlaid: {
      showNativeScrollbars: true,
    },
    overflowBehavior: {
      x: 'hidden',
    },
    paddingAbsolute: true,
    scrollbars: {
      autoHide: 'never',
      clickScrolling: true,
    },
  };

  public hospitalNames: HospitalNames;

  constructor(
    private rankingService: RankingService,
    private skillService: SkillService,
    private errorService: ErrorService,
    private popupManagerService: PopupManagerService,
    private userService: UserService,
    private destroyRef: DestroyRef,
    private subscriptionManager: SubscriptionManager,
    private instanceIdService: InstanceIdService
  ) {
    this.instanceId = this.instanceIdService.generateId();
    this.subscriptionManager.registerComponent(this.instanceId, this.destroyRef);
    this.hospitalNames = this.userService.getHospitalNames();
  }

  ngOnInit() {
    if (this.openType === 'EDIT') {
      this.getNurseSkills();
    } else {
      this.getSpecialtiesWithRanks();
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (undefined === changes['profile'].previousValue && undefined === changes['openType'].previousValue) {
      return;
    }
    this.isLoading = true;
    if (this.openType === 'EDIT') {
      this.getNurseSkills();
    } else {
      this.getSpecialtiesWithRanks();
    }
  }

  getNurseSkills() {
    const selectedHospital = this.userService.getSelectedHospitals();
    this.specialtiesWithRanks = [];
    this.skillsOfProfileFromOtherHospitals = [];
    this.listOfRatings = [];
    this.imgLinks = [];
    this.subscriptionManager
      .createRequest(this.skillService.getSkillsOfNurse(this.profile._id), 'getSkillsOfNurse', this.instanceId)
      .subscribe({
        next: (res: any) => {
          this.subscriptionManager
            .createRequest(
              this.rankingService.getSpecialtiesWithRanks({
                formation: this.profile.position,
                hospitalId: selectedHospital[0],
              }),
              'getSpecialtiesWithRanks',
              this.instanceId
            )
            .subscribe({
              next: (results: any) => {
                this.formatSkills(res, results);
                this.specialtiesWithRanks = this.specialtiesWithRanks.filter(
                  (item: any) => item.specialtyName !== 'Bariatrique'
                );
                this.setSpecialtiesIcons();
                this.isLoading = false;
              },
              error: (error: any) => this.errorService.handleError(error),
            });
        },
        error: (error: any) => this.errorService.handleError(error),
      });
  }

  formatSkills(list: any[], ratings: any) {
    ratings.forEach((specialty: any) => {
      specialty.ratings.forEach((rating: any) => {
        if (!this.listOfRatings.find((rating_: any) => String(rating_.role.name) === String(rating.role.name))) {
          this.listOfRatings.push(rating);
        }
      });
    });
    for (let index = 0; index < list.length; index++) {
      const skill = list[index];
      const rating = ratings.find((r: any) => String(r.specialty._id) === String(skill.specialty._id));

      if (rating) {
        skill.ratings = rating.ratings;
        skill.ratings.forEach((rating: any) => {
          if (String(rating.role._id) === String(skill.role._id)) {
            rating.nurseLevel = skill.level;
            rating.oldNurseLevel = skill.level;
            rating.tmpLevel = skill.level;
            rating.maxLevel = rating.level.length + 1;
            rating.timeIndicator = skill.timeIndicator;
          }
        });
      }
    }
    list = this.sortSpecialties(list);
    const skillsArray: any[] = [];
    for (const item of list) {
      if (!skillsArray.find((sk: any) => String(sk.specialtyId) === String(item.specialty._id))) {
        skillsArray.push({
          specialtyId: item.specialty._id,
          specialtyName: item.specialty.name,
          specialtyImage: item.specialty.image,
          ratings: item.ratings,
        });
      }
    }
    this.specialtiesWithRanks = skillsArray;
  }

  skillChanged(skill: any, rating: any) {
    const specialtyRole = this.getSpecialtyRole(skill, rating);
    if (this.openType === 'EDIT') {
      if (specialtyRole.tmpLevel === '0' || specialtyRole.tmpLevel === 0) {
        this.openConfirmationDialog(skill, specialtyRole);
      } else {
        if (specialtyRole.oldNurseLevel !== specialtyRole.nurseLevel) {
          specialtyRole.timeIndicator = 0;
        }
      }
      specialtyRole.tmpLevel = specialtyRole.nurseLevel;
    }
  }

  openConfirmationDialog(skill: any, rating: any): void {
    const description = `En modificant cette valeur vous autorisez ${this.profile.firstName} ${this.profile.lastName} à travailler en ${skill.specialtyName} en tant que ${rating.role.name}. Etes-vous sûr(e) de votre choix ?`;

    this.subscriptionManager
      .createRequest(
        this.popupManagerService
          .openConfirmationPopup('Autoriser la progression ?', description, 375, 'primary', 'Oui, autoriser')
          .afterClosed(),
        'openConfirmationDialog',
        this.instanceId
      )
      .subscribe({
        next: (result: any) => {
          if (!result) {
            rating.oldNurseLevel = '0';
            rating.nurseLevel = rating.oldNurseLevel;
          }
        },

        error: (error: any) => {
          this.errorService.handleError(error);
        },
      });
  }

  isSpecialtyHaveRole(specialty: any, role: any) {
    if (specialty && specialty.ratings) {
      for (let index = 0; index < specialty.ratings.length; index++) {
        const rating = specialty.ratings[index];
        if (String(rating.role.name) === String(role.role.name)) {
          return true;
        }
      }
      return false;
    }

    return false;
  }

  getSpecialtyRole(specialty: any, role: any) {
    for (let index = 0; index < specialty.ratings.length; index++) {
      const rating = specialty.ratings[index];
      if (String(rating.role.name) === String(role.role.name)) {
        return rating;
      }
    }
    return null;
  }

  getSpecialtiesWithRanks() {
    const observables = [];
    this.specialtiesWithRanks = [];
    this.skillsOfProfileFromOtherHospitals = [];
    this.listOfRatings = [];
    this.imgLinks = [];
    // observables.push(this.rankingService.getSpecialtiesWithRanks({ formation: this.profile.position, hospitalId: this.profile.hospitals[0] }));
    observables.push(
      this.rankingService.getSpecialtiesWithRanks({
        formation: this.profile.position,
        hospitalId: this.newServiceId ? this.newServiceId : this.profile.principalHospitalId,
      })
    );

    if (['NEW', 'ADD'].includes(this.openType) && this.profile && this.profile._id) {
      // this.profile && this.profile._id && this.openType === 'NEW' => Ajouter un service un profil
      // this.profile && this.profile._id && this.openType === 'ADD' => Importer un profil dans un service
      // this.profile && this.openType === 'ADD' => Créer un nouveau profil
      // this.profile && this.profile._id && this.openType === 'EDIT' => Modification des skills
      // observables.push(this.skillService.getSkillsOfProfileForHospitalFromOtherHospitals(this.profile._id, this.profile.hospitals[0]));
      observables.push(
        this.skillService.getSkillsOfProfileForHospitalFromOtherHospitals(
          this.profile._id,
          this.newServiceId ? this.newServiceId : this.profile.principalHospitalId
        )
      );
    }

    this.subscriptionManager
      .createRequest(forkJoin(observables), 'getSpecialtiesWithRanks', this.instanceId)
      .subscribe({
        next: (data: any) => {
          this.specialtiesWithRanks = data[0];
          this.skillsOfProfileFromOtherHospitals = data[1] || [];
          this.setSkillsFromOtherHospitals();
          this.getlistOfRatings();
          this.generateSkillsList(this.specialtiesWithRanks);
          this.setSpecialtiesIcons();
          if (!this.specialtiesWithRanks || this.specialtiesWithRanks.length <= 0) {
            this.skillsValidation.emit(this.specialtiesWithRanks);
          }
          this.isLoading = false;
        },

        error: (error: any) => {
          this.errorService.handleError(error);
        },
      });
  }

  setSkillsFromOtherHospitals() {
    for (const skill of this.skillsOfProfileFromOtherHospitals || []) {
      try {
        const foundSpecialty = this.specialtiesWithRanks.find((s: any) => String(s._id) === String(skill.specialty));

        if (foundSpecialty) {
          foundSpecialty.skillsInitializedFromOtherHospitals = true;
          const foundSkill = foundSpecialty.ratings.find((r: any) => String(r.role._id) === String(skill.role));

          if (foundSkill) {
            foundSkill.skillsInitializedFromOtherHospitals = true;
            foundSkill.nurseLevel = skill.level;
            foundSkill.tmpLevel = skill.level;
            foundSkill.timeIndicator = skill.timeIndicator;
          }
        }
      } catch (e) {
        console.error(e);
      }
    }
  }

  getlistOfRatings() {
    this.specialtiesWithRanks.forEach((specialty: any) => {
      specialty.ratings.forEach((rating: any) => {
        if (!this.listOfRatings.find((rating_: any) => String(rating_.role.name) === String(rating.role.name))) {
          this.listOfRatings.push(rating);
        }
      });
    });
  }

  generateSkillsList(list: any[]) {
    list = this.sortSpecialties(list);
    const skillsArray: any[] = [];
    this.specialtiesWithRanks.forEach((specialty: any) => {
      specialty.ratings.forEach((rating: any) => {
        if (!specialty.skillsInitializedFromOtherHospitals) {
          rating.nurseLevel = 0;
          rating.tmpLevel = 0;
        }
        rating.maxLevel = rating.level.length + 1;
      });
    });
    for (const item of list) {
      skillsArray.push({
        specialtyId: item.specialty._id,
        specialtyName: item.specialty.name,
        specialtyImage: item.specialty.image,
        ratings: item.ratings,
        skillsInitializedFromOtherHospitals: item.skillsInitializedFromOtherHospitals,
      });
    }
    this.specialtiesWithRanks = skillsArray;
  }

  sortSpecialties(skillsArray: any[]) {
    return skillsArray.sort((a: any, b: any) => {
      return a.specialty.priority - b.specialty.priority; //ERR
    });
  }

  generateListByLength(length: number) {
    const list: number[] = [];
    for (let i = 0; i < length; i++) {
      list.push(i + 1);
    }
    return list;
  }

  onSkillsValidation() {
    this.specialtiesWithRanks = this.specialtiesWithRanks.filter((specialty: any) => specialty.ratings);
    this.specialtiesWithRanks.forEach((specialty: any) => {
      specialty.ratings.forEach((rating: any) => {
        rating.nurseLevel = parseInt(rating.nurseLevel);
      });
    });
    this.skillsValidation.emit(this.specialtiesWithRanks);
  }

  setSpecialtiesIcons() {
    const specialtiesLinks: string[] = [];
    for (const specialty of this.specialtiesWithRanks) {
      specialtiesLinks.push(specialty.specialtyImage);
    }
    this.imgLinks = specialtiesLinks;
  }

  closeDialog(): void {
    this.onClose.emit();
  }
}
